import React from "react";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { IndianRupee, Calculator, AlertCircle } from "lucide-react";
import { CourseGroupForEnrollment } from "@/services/studentService";
import { formatCurrency } from "@/lib/model";

type YearWiseDiscount = {
  year: number;
  amount: string;
  reason: string;
};

interface YearwiseFeeBreakdownProps {
  courseGroup: CourseGroupForEnrollment;
  yearWiseDiscounts?: YearWiseDiscount[];
  showDiscountValidation?: boolean;
}

const YearwiseFeeBreakdown: React.FC<YearwiseFeeBreakdownProps> = ({
  courseGroup,
  yearWiseDiscounts = [],
  showDiscountValidation = true,
}) => {
  const { yearWiseFees, name } = courseGroup;
  
  // Calculate totals
  const totalFee = yearWiseFees.reduce((sum, fee) => sum + fee.feeAmount, 0);
  const totalDiscount = yearWiseDiscounts.reduce(
    (sum, discount) => sum + (parseFloat(discount.amount) || 0),
    0
  );
  
  // Check if any discount exceeds its year fee
  const hasInvalidDiscount = yearWiseDiscounts.some((discount) => {
    const yearFee = yearWiseFees.find(fee => fee.yearNumber === discount.year);
    return yearFee && parseFloat(discount.amount) > yearFee.feeAmount;
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <IndianRupee className="h-5 w-5 text-green-600" />
          Year-wise Fee Structure - {name}
        </CardTitle>
        <p className="text-sm text-gray-600">
          This fee structure will be locked for the student at enrollment time.
        </p>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Year-wise Breakdown */}
          <div className="space-y-3">
            {yearWiseFees.map((fee, index) => {
              const discount = yearWiseDiscounts.find(d => d.year === fee.yearNumber);
              const discountAmount = parseFloat(discount?.amount || "0");
              const finalAmount = Math.max(0, fee.feeAmount - discountAmount);
              
              return (
                <motion.div
                  key={fee.yearNumber}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={`p-3 rounded-lg border ${
                    discount ? 'bg-orange-50 border-orange-200' : 'bg-gray-50 border-gray-200'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <Badge variant={discount ? "default" : "outline"}>
                        Year {fee.yearNumber}
                      </Badge>
                      {discount && (
                        <span className="text-xs text-orange-600 font-medium">
                          (Discount applied)
                        </span>
                      )}
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-semibold text-gray-600">
                        {formatCurrency(fee.feeAmount)}
                      </div>
                      {discount && discountAmount > 0 && (
                        <div className="text-xs text-orange-600">
                          -{formatCurrency(discountAmount)}
                        </div>
                      )}
                    </div>
                  </div>
                  {discount && discountAmount > 0 && (
                    <div className="flex items-center justify-between text-sm border-t pt-2 mt-2">
                      <span className="font-medium text-gray-700">Year {fee.yearNumber} Final:</span>
                      <span className="font-bold text-green-600">
                        {formatCurrency(finalAmount)}
                      </span>
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>

          {/* Total Fee Summary */}
          <div className="border-t pt-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Calculator className="h-4 w-4 text-gray-600" />
                <span className="font-medium">Total Course Fee</span>
              </div>
              <span className="text-xl font-bold text-green-600">
                {formatCurrency(totalFee)}
              </span>
            </div>
            
            {totalDiscount > 0 && (
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-gray-600">
                  Total Discount Applied
                </span>
                <span className="text-lg font-semibold text-orange-600">
                  -{formatCurrency(totalDiscount)}
                </span>
              </div>
            )}
            
            <div className="flex items-center justify-between border-t pt-2">
              <span className="font-semibold">Final Amount Payable</span>
              <span className="text-2xl font-bold text-blue-600">
                {formatCurrency(Math.max(0, totalFee - totalDiscount))}
              </span>
            </div>
          </div>

          {/* Discount Validation Warning */}
          {showDiscountValidation && hasInvalidDiscount && (
            <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
              <AlertCircle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
              <div className="text-sm text-red-800">
                <p className="font-medium">Invalid Discount Amount</p>
                <p>
                  One or more discounts exceed their respective year fees. Please adjust the discount amounts.
                </p>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default YearwiseFeeBreakdown;
